/* KANON `Programy v2 - D3 (0.8)` — the assigned-clients strip, transcribed.
 *
 * Read off the rendered specimen at 1512x1000, and from its own `avStackD` / `v.assignCompact`:
 *   section  0 19 padding
 *   card     41.5 tall, 0 11 padding, 9 gap, radius 9.5, --bg-inset under a --border hairline
 *   face     25.5, a circle, --chip-solid-fill over --chip-solid-ink, a 2px ring (D20: --bg-inset),
 *            --shadow-pl-face, 9 / 700 / --ls-nav, each disc 5px over the one before it
 *   +N       the same disc at 800 and tabular figures, LAST in the stack
 *   sentence 10 / 600, --text where somebody follows the plan and --text-3 where nobody does
 *   link     9.5 / 700, --text-2, flush right
 *   list     37px rows, a 22.5 disc, 9 gap, a --border rule under each, name 10 / 600
 *
 * ── The TEMPLATE's strip is a LIST now, the client's is still the card (owner, 16 Sept 2026) ──
 * The card, the stack and the disclosure below are kept because `ClientStrip` still draws all three
 * for its one person — see the component's header for why one fact belongs in a card. Everything
 * under `── The open list ──` is the template strip's own, and the specimen's 37px row is the row
 * it reuses.
 *
 * ── The stack overlaps by FIVE and stacks front-to-back ──────────────────────────────────────
 * `margin-left: -5px` on every disc but the first, and a descending `z-index`, so the first face is
 * on top and `+N` sits behind them all. Without the z-order the last disc would overlap the ones
 * before it and the stack would read backwards.
 *
 * ── The ring's colour is the CARD's, which is the specimen's own value ───────────────────────
 * `border: 2px solid T.card`. It separates three overlapping discs in Flame Dark, where the ring is
 * `#161618` against a white disc. In Flame it is `#ffffff` against a white disc — the same colour,
 * so the three faces merge into one lozenge. That is a measured failure of the specimen and it is
 * REPORTED with its number rather than silently corrected: swapping a KANON colour needs the
 * owner's word (`--bg-inset`, the strip card's own ground, is the one-token fix). Recorded here so
 * the next reader does not mistake it for a transcription slip.
 *
 * **Every text leaf states its line-height.** `base.css` puts `--lh-relaxed` on `body` and the
 * specimen's body carries none, so an unstated leaf renders about 2px taller than the canon.
 *
 * Heights and widths are layout rather than design tokens (see stylelint.config.js); every colour,
 * space, radius and type value below is a token. */

.section {
  display: flex;
  flex: none;
  flex-direction: column;
  padding: var(--space-0) var(--space-19px);
}

.card {
  display: flex;
  flex: none;
  gap: var(--space-9px);
  align-items: center;
  height: var(--h-pl-strip);
  padding: var(--space-0) var(--space-11px);
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-9-5px);
}

/* ── The faces ──────────────────────────────────────────────────────────────────────────────── */

.stack {
  display: flex;
  flex: none;
  align-items: center;
}

.face {
  display: grid;
  flex: none;
  place-items: center;
  width: var(--sz-pl-avatar);
  height: var(--sz-pl-avatar);
  overflow: hidden;
  font-size: var(--fs-pl-micro);
  font-weight: var(--fw-bold);
  line-height: var(--lh-none);
  letter-spacing: var(--ls-nav);
  color: var(--chip-solid-ink);
  background: var(--chip-solid-fill);

  /* Two pixels, which is what the specimen writes. `--border-focus` is the app's only 2px width
   * token; it is a WIDTH here and nothing to do with focus.
   *
   * `--bg-inset` rather than the specimen's `--bg-card`: owner decision D20, 3 September 2026. In
   * Flame the card's own ground is white, the discs are white, and the ring between them is white
   * too — three faces merge into one lozenge and the stack stops reading as a stack. The inset step
   * is a KANON colour and is the one-token fix; in Flame Dark the two are close enough that the
   * ring reads as it did. */
  border: var(--border-focus) solid var(--bg-inset);
  border-radius: var(--r-circle);
  box-shadow: var(--shadow-pl-face);
}

/* The overlap and the `+N` bubble are GONE with the template's stack (owner, 16 September 2026):
 * `.stack` now only ever holds `ClientStrip`'s single face, so there is nothing to overlap and
 * nothing to count. Kept as a one-disc box rather than dissolved into the card, because the disc
 * still needs its own flex item beside a sentence that may ellipsise. */

/* The reserve, standing at the loaded diameter while the assignment read is open. `background:
 * transparent` so an unlit disc is not three white circles claiming to be people; the skeleton's
 * own fill is applied on top of it by `skeleton.module.css` when the gate opens. */
.faceReserved {
  background: transparent;
  box-shadow: none;
}

/* ── The sentence, and the way into the list ─────────────────────────────────────────────────── */

.sentence {
  flex: none;
  font-size: var(--fs-pl-body);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-auto);
  color: var(--text);
  white-space: nowrap;
}

/* Nobody follows this plan. `--text-3` is the specimen's own `T.t3` for exactly this case, and the
 * sentence says so in words — the state is never carried by the colour alone. */
.nobody {
  flex: none;
  font-size: var(--fs-pl-body);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-auto);
  color: var(--text-3);
  white-space: nowrap;
}

.spacer {
  flex: 1;
}

/* A `<button>` on the template's strip and a `<Link>` on the client's: one is a disclosure, the
 * other a navigation, and they wear the same 9.5px label in the same place. `text-decoration` is the
 * one declaration the button did not need. */
.toggle {
  flex: none;
  padding: var(--space-0);
  font-size: var(--fs-micro-label);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  color: var(--text-2);
  white-space: nowrap;
  background: transparent;
  border: none;
  cursor: pointer;
  text-decoration: none;
}

.toggle:hover {
  color: var(--text);
}

.toggle:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--outline-inset);
}

/* The assignment read failed, so the names cannot be listed at all. It stands at the TOP of the
 * reserve rather than centred in it: the first row is where the eye already is, and a sentence
 * floating in the middle of an eight-row box reads as a thing that lost its list.
 *
 * `--text-3` because it is a statement of fact rather than something to press, and it is WORDS, so
 * the state is never carried by the colour alone. */
.note {
  margin: var(--space-0);
  padding: var(--space-11px) var(--space-0) var(--space-0);
  font-size: var(--fs-micro-label);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  color: var(--text-3);
  white-space: nowrap;
}

/* The list row's smaller disc. The only survivor of the table the disclosure used to expand: its
 * 22.5px diameter is the specimen's own figure for a row, and the open list keeps the row. */
.faceSm {
  width: 22.5px;
  height: 22.5px;
  font-size: var(--fs-pl-eyebrow);
}

/* ── The open list ─────────────────────────────────────────────────────────────────────────────
 *
 * The section is `flex: none` and takes the height its rows need: unlike the two sections above it
 * this one has nothing under it, so it is the pane's last block and growing it scrolls rather than
 * pushes. Its reserve is the ROW COUNT, which the component takes from a figure that never waits.
 */

/* An `h3` styled as the canon's eyebrow, matching the week and the muscle split above it. The
 * heading's user-agent margin and size are both taken off: nothing about the box may move because
 * the element changed. */
.eyebrow {
  margin: var(--space-0) var(--space-0) var(--space-2);
  font-size: var(--fs-pl-eyebrow);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  letter-spacing: var(--ls-eyebrow);
  color: var(--text-3);
  text-transform: uppercase;
}

/* The RESERVE. `--strip-rows` is set on the element by the component, from a count that arrives
 * with the screen, so this box stands at its loaded height before a single name is known — and the
 * three states inside it (the wait, the names, the failure) all fit in one box rather than three.
 *
 * A row is 37px and the list has no gaps, so the height is the row count times the row. The rule
 * against literals is about design values; a row height is layout (see stylelint.config.js). */
.body {
  min-height: calc(var(--strip-rows) * 37px);
}

.list {
  display: flex;
  flex-direction: column;
  margin: var(--space-0);
  padding: var(--space-0);
  list-style: none;
}

/* The specimen's 37px row with its rule underneath. The LAST row keeps it too, so a list that ran
 * out reads as closed rather than as cut off. */
.person {
  border-bottom: var(--border-hairline) solid var(--border);
}

.name {
  flex: 1;
  overflow: hidden;
  font-size: var(--fs-pl-body);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-auto);
  color: var(--text);
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* A direction, not a control: it says the row goes somewhere and takes the row's own click. Muted
 * until the row is under the pointer, so eight of them do not draw eight arrows at the eye. */
.chevron {
  flex: none;
  width: var(--icon-xs);
  height: var(--icon-xs);
  color: var(--text-3);
}

/* The whole row is one target. `display: flex` on the link rather than on the `li`, so the hit area
 * and the focus ring are the row and not a name inside it. */
.personBody {
  display: flex;
  gap: var(--space-9px);
  align-items: center;
  height: 37px;
  padding: var(--space-0);
  color: inherit;
  text-decoration: none;
}

.personBody:hover .name {
  text-decoration: underline;
}

.personBody:hover .chevron {
  color: var(--text-2);
}

/* Inset, because the row is flush with the pane's own padding: an outline drawn outside it would be
 * clipped by the scroller the section stands in. */
.personBody:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--outline-inset);
}

/* The rest of the roster, appended rather than revealed — see the component's header. It sits under
 * the last row's rule, in the list's own left margin, so it reads as the continuation of the list
 * rather than as a control over it. */
.showAll {
  align-self: flex-start;
  padding: var(--space-9px) var(--space-0) var(--space-0);
  font-size: var(--fs-micro-label);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  color: var(--text-2);
  background: transparent;
  border: none;
  cursor: pointer;
}

.showAll:hover {
  color: var(--text);
}

.showAll:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

/* The bar standing in for a name while the assignment read is open. A fixed width rather than a
 * share of the row: the names it replaces are not all the same length either, and a bar that grew
 * with the pane would be the one part of the wait that moved. */
.skelName {
  width: 118px;
  height: 10px;
}
